import { useEffect, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { Alert, AlertTitle, Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { fetchAuthMethods, type AuthMethod, type AuthMethodsResponse } from '#app/api/auth'
import { displayName, fetchSession, logout, type SessionInfo } from '#app/api/session'
import { sanitizeOriginalUrl } from '#app/util/originalUrl'
import AuthLayout from '#components/AuthLayout'

export default function LoginPage() {
    const { t, i18n } = useTranslation()
    const [searchParams] = useSearchParams()
    // Reject attacker-supplied off-platform URLs before we ever hand the
    // value to window.location.assign or forward it to the server. Open
    // redirects from /login post-auth would let a phishing site pose as
    // a logged-in Fixrate page.
    const originalUrl = sanitizeOriginalUrl(searchParams.get('originalUrl'))
    // The auth methods are localised by the backend, so request them in the
    // user's resolved language (URL ?lng → cookie → browser → nb).
    const lang = i18n.language
    const realm = searchParams.get('realm') ?? undefined
    const method = searchParams.get('method') ?? undefined

    const [session, setSession] = useState<SessionInfo | null>(null)
    const [methods, setMethods] = useState<AuthMethodsResponse | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [showOptional, setShowOptional] = useState(false)
    const [switchingUser, setSwitchingUser] = useState(false)

    useEffect(() => {
        let cancelled = false
        fetchSession()
            .then((info) => {
                if (cancelled) return
                setSession(info)
                if (!info.authenticated) {
                    // Only fetch methods when we know we need them.
                    fetchAuthMethods({ lang, realm, method, originalUrl })
                        .then((data) => { if (!cancelled) setMethods(data) })
                        .catch((err: Error) => { if (!cancelled) setError(err.message) })
                }
            })
            .catch((err: Error) => { if (!cancelled) setError(err.message) })
        return () => {
            cancelled = true
        }
    }, [lang, realm, method, originalUrl])

    const onContinue = () => {
        window.location.assign(originalUrl || '/')
    }

    const onSwitchUser = async () => {
        setSwitchingUser(true)
        try {
            await logout()
        } catch {
            // Even if logout fails server-side, drop the local session view and
            // reload so the picker shows. The cookie may linger; users can try
            // again or contact support.
        }
        window.location.reload()
    }

    if (error) {
        return (
            <AuthLayout>
                <Alert severity="error">
                    <AlertTitle>{t('errorTitle')}</AlertTitle>
                    {error}
                </Alert>
            </AuthLayout>
        )
    }

    if (!session) {
        return (
            <AuthLayout>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            </AuthLayout>
        )
    }

    if (session.authenticated) {
        return (
            <AuthLayout>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h1" component="h1" gutterBottom>
                            {t('signedInHeading')}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            <Trans i18nKey="signedInAs" values={{ name: displayName(session) }} components={[<b key="name" />]} />
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" onClick={onContinue} disabled={switchingUser}>
                            {t('continue')}
                        </Button>
                        <Button variant="outlined" onClick={onSwitchUser} disabled={switchingUser}>
                            {t('switchUser')}
                        </Button>
                    </Stack>
                </Stack>
            </AuthLayout>
        )
    }

    if (!methods) {
        return (
            <AuthLayout>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            </AuthLayout>
        )
    }

    const primary = methods.primary
    const optional = methods.optional

    return (
        <AuthLayout>
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h1" component="h1" gutterBottom>
                        {t('loginHeading')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('loginBankIDMessage')}
                    </Typography>
                </Box>

                <MethodList>
                    {primary.map((m) => (
                        <MethodRow key={m.href} method={m} />
                    ))}
                    {showOptional && optional.map((m) => <MethodRow key={m.href} method={m} />)}
                </MethodList>

                {optional.length > 0 && !showOptional && (
                    <Box sx={{ textAlign: 'center' }}>
                        <Button variant="text" size="small" onClick={() => setShowOptional(true)}>
                            {t('showOtherMethods')}
                        </Button>
                    </Box>
                )}
            </Stack>
        </AuthLayout>
    )
}

// The sign-in methods sit in one bordered list with hairline dividers —
// mirroring fixrate-security's portal.ftlh (the marketplace login iframe),
// not separated cards.
function MethodList({ children }: { children: ReactNode }) {
    return (
        <Box
            sx={{
                border: '1px solid #f1f1f1',
                borderRadius: '4px',
                overflow: 'hidden',
                '& > a': { borderBottom: '1px solid #f1f1f1' },
                '& > a:last-of-type': { borderBottom: 'none' },
            }}
        >
            {children}
        </Box>
    )
}

// A single sign-in method, matching the portal row: a fixed-width logo tile
// that stays white at all times, and a text part (with trailing chevron) that
// is the only piece whose background changes on hover.
function MethodRow({ method }: { method: AuthMethod }) {
    return (
        <Box
            component="a"
            href={method.href}
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                textDecoration: 'none',
                color: '#21362C',
                // Only the text part (.button) reacts to hover.
                '&:hover .method-text': { bgcolor: '#f2f2f2' },
                '&:hover .method-chevron': { transform: 'translateX(5px)' },
            }}
        >
            <Box
                className="method-logo"
                sx={{
                    width: '8rem',
                    flexShrink: 0,
                    bgcolor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 1.8,
                }}
            >
                <Box
                    component="img"
                    src={method.logo}
                    alt=""
                    sx={{ maxWidth: '6rem', maxHeight: '2.4rem', objectFit: 'contain' }}
                />
            </Box>
            <Box
                className="method-text"
                sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: '#f9f9f9',
                    px: 2,
                    py: 1.8,
                    fontWeight: 700,
                    transition: 'background-color 150ms ease',
                }}
            >
                <Box sx={{ flex: 1 }}>{method.description}</Box>
                <ChevronRightIcon
                    className="method-chevron"
                    sx={{ color: '#21362C', transition: 'transform 150ms ease' }}
                />
            </Box>
        </Box>
    )
}
